import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Calendar, Plus, Trash, Warning, ChalkboardTeacher, Users, ClipboardText, Check,
} from "@phosphor-icons/react";

const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
const HOURS = Array.from({ length: 11 }, (_, i) => `${(7 + i).toString().padStart(2, "0")}:00`); // 07:00..17:00

export default function Timetable() {
  const { user } = useAuth();
  const isStudent = user?.role === "student";
  const canEdit = user && ["super_admin","owner","admin","director","study_director","secretary","teacher"].includes(user.role);
  const [slots, setSlots] = useState([]);
  const [courses, setCourses] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [selClass, setSelClass] = useState("");
  const [selTeacher, setSelTeacher] = useState("");
  const [selSubject, setSelSubject] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ course_id: "", day_of_week: 0, start_time: "08:00", end_time: "09:00", room: "", week_pattern: "weekly" });
  const [conflicts, setConflicts] = useState([]);
  const [openAtt, setOpenAtt] = useState(null); // {course_id, date, students, subject, class_group}
  const [attMarks, setAttMarks] = useState({});

  const loadAll = async () => {
    try {
      const [cr, cl, s, u] = await Promise.all([
        api.get("/courses"),
        api.get("/classes"),
        api.get("/subjects"),
        // /teachers is accessible to any user of the school (fixes 403 for teachers/students)
        api.get("/teachers"),
      ]);
      setCourses(cr.data); setClasses(cl.data); setSubjects(s.data);
      setTeachers(u.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const loadSlots = async () => {
    try {
      // Backend already scopes slots for students to their enrolled classes.
      // We fetch all scoped slots then filter locally by class/teacher/subject.
      const r = await api.get("/timetable/week");
      setSlots(r.data.slots || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadAll(); }, []);
  useEffect(() => { loadSlots(); /* eslint-disable-next-line */ }, []);

  const cById = useMemo(() => Object.fromEntries(courses.map(c => [c.id, c])), [courses]);
  const clById = useMemo(() => Object.fromEntries(classes.map(c => [c.id, c])), [classes]);
  const sById = useMemo(() => Object.fromEntries(subjects.map(s => [s.id, s])), [subjects]);
  const tById = useMemo(() => Object.fromEntries(teachers.map(t => [t.id, t])), [teachers]);

  // For students: derive available filter options ONLY from their own scoped slots.
  // For others: full school list.
  const availableClasses = useMemo(() => {
    if (!isStudent) return classes;
    const ids = new Set(slots.map(s => s.class_group?.id).filter(Boolean));
    return classes.filter(c => ids.has(c.id));
  }, [isStudent, slots, classes]);
  const availableTeachers = useMemo(() => {
    if (!isStudent) return teachers;
    const ids = new Set(slots.flatMap(s => (s.teachers || []).map(t => t.id)).filter(Boolean));
    return teachers.filter(t => ids.has(t.id));
  }, [isStudent, slots, teachers]);
  const availableSubjects = useMemo(() => {
    if (!isStudent) return subjects;
    const ids = new Set(slots.map(s => s.subject?.id).filter(Boolean));
    return subjects.filter(s => ids.has(s.id));
  }, [isStudent, slots, subjects]);

  // Local filtering applied on top of the (already scoped) slots
  const filteredSlots = useMemo(() => {
    return slots.filter(s => {
      if (selClass && s.class_group?.id !== selClass) return false;
      if (selTeacher && !(s.teachers || []).some(t => t.id === selTeacher)) return false;
      if (selSubject && s.subject?.id !== selSubject) return false;
      return true;
    });
  }, [slots, selClass, selTeacher, selSubject]);

  const save = async (force = false) => {
    try {
      const url = `/timetable-slots${force ? "?force=true" : ""}`;
      await api.post(url, form);
      toast.success("Créneau créé");
      setOpen(false); setConflicts([]);
      setForm({ course_id: "", day_of_week: 0, start_time: "08:00", end_time: "09:00", room: "", week_pattern: "weekly" });
      loadSlots();
    } catch (e) {
      if (e?.response?.status === 409) {
        const c = e.response.data?.detail?.conflicts || [];
        setConflicts(c);
        toast.error(`${c.length} conflit(s) détecté(s). Choisissez de forcer ou d'ajuster.`);
      } else toast.error(formatApiError(e));
    }
  };
  const del = async (s) => {
    if (!window.confirm("Supprimer ce créneau ?")) return;
    try { await api.delete(`/timetable-slots/${s.id}`); loadSlots(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // Attendance
  const openAttendance = async (slot) => {
    const course = cById[slot.course_id];
    if (!course) return;
    try {
      // Fetch enrolled students of the class group via workspace
      const ws = await api.get(`/courses/${course.id}/workspace`);
      const today = new Date().toISOString().slice(0, 10);
      const marks = {};
      ws.data.students.forEach(st => { marks[st.id] = "present"; });
      // Load existing marks if any
      try {
        const existing = await api.get(`/sessions/${course.id}/attendance?date=${today}`);
        for (const m of existing.data.marks || []) marks[m.student_id] = m.status;
      } catch (_) { /* ignore */ }
      setOpenAtt({
        course_id: course.id, date: today,
        students: ws.data.students, subject: ws.data.subject, class_group: ws.data.class_group,
        slot,
      });
      setAttMarks(marks);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveAttendance = async () => {
    if (!openAtt) return;
    try {
      const marks = Object.entries(attMarks).map(([student_id, status]) => ({ student_id, status }));
      await api.post("/sessions/take-attendance", { course_id: openAtt.course_id, date: openAtt.date, marks });
      toast.success(`Appel enregistré · ${marks.length} élèves`);
      setOpenAtt(null); setAttMarks({});
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // build grid: {day_of_week: [slots sorted by start_time]}
  const grid = {};
  for (const s of filteredSlots) {
    (grid[s.day_of_week] ||= []).push(s);
  }
  Object.values(grid).forEach(arr => arr.sort((a, b) => a.start_time.localeCompare(b.start_time)));

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto">
      <header className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-secondary font-semibold">Planning</div>
          <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
            <Calendar size={32} weight="duotone" className="text-primary" />
            Emploi du temps
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isStudent
              ? "Consultation de votre emploi du temps. Pour ajouter un rappel personnel, utilisez la page Agenda."
              : "Grille hebdomadaire · détection auto des conflits salle/prof · appel en 1 clic"}
          </p>
        </div>
        {canEdit && (
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setConflicts([]); }}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-new-slot">
              <Plus size={16} className="mr-2" /> Nouveau créneau
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>Nouveau créneau</DialogTitle>
              <DialogDescription>Ajoutez un cours au planning hebdomadaire.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-3">
              <div>
                <Label>Cours</Label>
                <Select value={form.course_id} onValueChange={v => setForm({ ...form, course_id: v })}>
                  <SelectTrigger data-testid="select-slot-course"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent>
                    {courses.map(c => {
                      const cl = clById[c.class_group_id]; const sj = sById[c.subject_id];
                      return <SelectItem key={c.id} value={c.id}>{sj?.name || "?"} · {cl?.name || "?"}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Jour</Label>
                  <Select value={String(form.day_of_week)} onValueChange={v => setForm({ ...form, day_of_week: Number(v) })}>
                    <SelectTrigger data-testid="select-slot-day"><SelectValue /></SelectTrigger>
                    <SelectContent>{DAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Début</Label><Input type="time" value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} data-testid="input-slot-start" /></div>
                <div><Label>Fin</Label><Input type="time" value={form.end_time} onChange={e => setForm({ ...form, end_time: e.target.value })} data-testid="input-slot-end" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Salle</Label><Input value={form.room} onChange={e => setForm({ ...form, room: e.target.value })} data-testid="input-slot-room" /></div>
                <div>
                  <Label>Semaine</Label>
                  <Select value={form.week_pattern} onValueChange={v => setForm({ ...form, week_pattern: v })}>
                    <SelectTrigger data-testid="select-slot-pattern"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Toutes les semaines</SelectItem>
                      <SelectItem value="A">Semaine A</SelectItem>
                      <SelectItem value="B">Semaine B</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {conflicts.length > 0 && (
                <div className="rounded-md border border-accent bg-accent/5 p-3 text-sm">
                  <div className="flex items-center gap-2 font-semibold text-accent"><Warning size={16} weight="fill" /> {conflicts.length} conflit(s)</div>
                  <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                    {conflicts.map((c, i) => (
                      <li key={i}>• {DAYS[c.day_of_week]} {c.start_time}–{c.end_time} · {c.reason === "room" ? `Salle ${c.room} occupée` : c.reason === "teacher" ? "Enseignant déjà en cours" : "Classe déjà en cours"}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              {conflicts.length > 0 && <Button variant="outline" className="border-accent text-accent" onClick={() => save(true)} data-testid="btn-force-slot">Forcer quand même</Button>}
              <Button onClick={() => save(false)} data-testid="btn-save-slot">Créer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        )}
      </header>

      {/* Filters */}
      <div className="mb-4 flex gap-2 flex-wrap">
        <Select value={selClass || "all"} onValueChange={v => setSelClass(v === "all" ? "" : v)}>
          <SelectTrigger className="w-48" data-testid="filter-class"><SelectValue placeholder={isStudent ? "Ma classe" : "Toutes classes"} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{isStudent ? "Toutes mes classes" : "Toutes les classes"}</SelectItem>
            {availableClasses.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={selSubject || "all"} onValueChange={v => setSelSubject(v === "all" ? "" : v)}>
          <SelectTrigger className="w-48" data-testid="filter-subject"><SelectValue placeholder={isStudent ? "Mes matières" : "Toutes matières"} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{isStudent ? "Toutes mes matières" : "Toutes les matières"}</SelectItem>
            {availableSubjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={selTeacher || "all"} onValueChange={v => setSelTeacher(v === "all" ? "" : v)}>
          <SelectTrigger className="w-48" data-testid="filter-teacher"><SelectValue placeholder={isStudent ? "Mes enseignants" : "Tous enseignants"} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{isStudent ? "Tous mes enseignants" : "Tous les enseignants"}</SelectItem>
            {availableTeachers.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {(selClass || selSubject || selTeacher) && (
          <Button variant="ghost" size="sm" onClick={() => { setSelClass(""); setSelSubject(""); setSelTeacher(""); }} data-testid="btn-reset-filters">
            Réinitialiser
          </Button>
        )}
      </div>

      {/* Grid */}
      <Card className="overflow-x-auto">
        <div className="min-w-[900px]">
          <div className="grid grid-cols-7 border-b border-border bg-muted/40 text-xs uppercase tracking-wider">
            <div className="px-3 py-2 border-r border-border">Horaire</div>
            {DAYS.map(d => <div key={d} className="px-3 py-2 border-r border-border last:border-0 font-semibold">{d}</div>)}
          </div>
          {HOURS.map(h => (
            <div key={h} className="grid grid-cols-7 border-b border-border last:border-0 min-h-[70px]">
              <div className="px-3 py-2 border-r border-border text-xs text-muted-foreground bg-muted/20 tabular-nums">{h}</div>
              {DAYS.map((_, di) => {
                const cell = (grid[di] || []).filter(s => s.start_time <= h && s.end_time > h);
                return (
                  <div key={di} className="px-1.5 py-1.5 border-r border-border last:border-0 space-y-1">
                    {cell.map(s => {
                      const c = cById[s.course_id];
                      const cl = clById[c?.class_group_id];
                      const sj = sById[c?.subject_id];
                      const t = tById[(c?.teacher_ids || [])[0]];
                      const started = s.start_time === h;
                      if (!started) return null;
                      return (
                        <div key={s.id}
                          className={`rounded-md bg-primary/10 border-l-4 border-primary p-2 text-[11px] leading-tight group ${isStudent ? "" : "cursor-pointer hover:bg-primary/15"}`}
                          style={{ minHeight: `${(parseInt(s.end_time) - parseInt(s.start_time)) * 68 + (parseInt(s.end_time) - parseInt(s.start_time) - 1) * 4}px` }}
                          onClick={() => { if (!isStudent) openAttendance(s); }}
                          data-testid={`slot-${s.id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="font-heading font-semibold text-primary truncate">{sj?.name || "?"}</div>
                            {canEdit && (
                            <Button variant="ghost" size="icon" className="h-4 w-4 opacity-0 group-hover:opacity-100"
                                    onClick={(e) => { e.stopPropagation(); del(s); }}>
                              <Trash size={11} className="text-destructive" />
                            </Button>
                            )}
                          </div>
                          <div className="text-[10px] text-muted-foreground truncate">{cl?.name || "—"}</div>
                          <div className="text-[10px] text-muted-foreground truncate">{t?.name || "—"}</div>
                          <div className="mt-1 flex items-center gap-2 text-[9px] text-primary">
                            <span className="tabular-nums">{s.start_time}–{s.end_time}</span>
                            {s.room && <Badge variant="outline" className="text-[9px] py-0 h-4">{s.room}</Badge>}
                          </div>
                          {!isStudent && (
                          <div className="mt-1 text-[9px] text-accent font-semibold flex items-center gap-1 opacity-0 group-hover:opacity-100">
                            <ClipboardText size={10} /> Cliquer pour appel
                          </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </Card>

      <div className="mt-4 text-xs text-muted-foreground">
        {filteredSlots.length} créneau(x) affiché(s){slots.length !== filteredSlots.length ? ` sur ${slots.length}` : ""}. {isStudent ? "Vue restreinte à vos classes." : "Clic sur un créneau ouvre l'appel du jour."}
      </div>

      {/* Attendance dialog */}
      <Dialog open={!!openAtt} onOpenChange={(v) => { if (!v) { setOpenAtt(null); setAttMarks({}); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Appel — {openAtt?.subject?.name} · {openAtt?.class_group?.name}</DialogTitle>
            <DialogDescription>Date : {openAtt?.date} · marquez chaque élève (par défaut présent).</DialogDescription>
          </DialogHeader>
          <div className="max-h-[420px] overflow-y-auto space-y-1.5">
            {(openAtt?.students || []).map((s, i) => (
              <div key={s.id} className={`flex items-center justify-between p-2 rounded ${i % 2 ? "bg-muted/30" : ""}`}
                   data-testid={`att-row-${s.id}`}>
                <div className="text-sm">
                  <span className="font-mono text-xs text-muted-foreground mr-2">{s.matricule}</span>
                  <b>{s.last_name}</b> {s.first_name}
                </div>
                <div className="flex gap-1">
                  {["present", "absent", "late", "excused"].map(st => {
                    const active = attMarks[s.id] === st;
                    const label = { present: "P", absent: "A", late: "R", excused: "E" }[st];
                    const color = { present: "bg-secondary text-secondary-foreground", absent: "bg-accent text-accent-foreground", late: "bg-primary/60 text-primary-foreground", excused: "bg-muted text-muted-foreground" }[st];
                    return (
                      <button key={st} onClick={() => setAttMarks(m => ({ ...m, [s.id]: st }))}
                              className={`w-8 h-8 rounded text-xs font-bold ${active ? color : "bg-muted/40 hover:bg-muted text-muted-foreground"}`}
                              title={st} data-testid={`att-${s.id}-${st}`}>{label}</button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenAtt(null)}>Annuler</Button>
            <Button onClick={saveAttendance} className="bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-save-attendance">
              <Check size={16} className="mr-2" /> Enregistrer l'appel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
